import React, { useState, useEffect, useRef } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  TimeScale,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import 'chartjs-adapter-date-fns';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  TimeScale,
  Tooltip,
  Legend,
  Filler
);

const BitcoinPriceChart = ({ onError }) => {
  const [candleData, setCandleData] = useState([]);
  const [currentPrice, setCurrentPrice] = useState(null);
  const [interval, setInterval] = useState('1m');
  const [isConnected, setIsConnected] = useState(false);
  const [isLoadingHistory, setIsLoadingHistory] = useState(true);
  const [connectionAttempts, setConnectionAttempts] = useState(0);
  const [hasError, setHasError] = useState(false);
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const reconnectAttempts = useRef(0);

  const maxDataPoints = 100;

  const intervalOptions = [
    { value: '1m', label: '1 Minute' },
    { value: '5m', label: '5 Minutes' },
    { value: '15m', label: '15 Minutes' },
    { value: '1h', label: '1 Hour' },
  ];

  const fetchHistoricalData = async (retryCount = 0) => {
    setIsLoadingHistory(true);
    try {
      const limit = 100;
      const endTime = Date.now();
      const url = `https://api.binance.com/api/v3/klines?symbol=BTCUSDT&interval=${interval}&limit=${limit}&endTime=${endTime}`;
      
      console.log(`📊 Fetching historical data (attempt ${retryCount + 1})...`);
      
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout
      
      const response = await fetch(url, {
        signal: controller.signal,
        headers: {
          'Accept': 'application/json',
        }
      });
      
      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();

      if (Array.isArray(data) && data.length > 0) {
        const historicalCandles = data.map(candle => ({
          t: new Date(candle[0]),
          o: parseFloat(candle[1]),
          h: parseFloat(candle[2]),
          l: parseFloat(candle[3]),
          c: parseFloat(candle[4]),
        }));
        
        setCandleData(historicalCandles);
        setHasError(false);
        
        if (historicalCandles.length > 0) {
          setCurrentPrice(historicalCandles[historicalCandles.length - 1].c);
        }
        
        console.log(`✅ Historical data loaded: ${historicalCandles.length} candles`);
      } else {
        throw new Error('Invalid data format received');
      }
    } catch (error) {
      console.error(`❌ Error fetching historical data (attempt ${retryCount + 1}):`, error);
      setHasError(true);
      
      // Retry logic for Safari mobile issues
      if (retryCount < 3) {
        const delay = Math.min(1000 * Math.pow(2, retryCount), 5000);
        console.log(`🔄 Retrying historical data fetch in ${delay / 1000}s...`);
        
        setTimeout(() => {
          fetchHistoricalData(retryCount + 1);
        }, delay);
      } else if (onError) {
        console.log('🚨 Historical data fetch failed after retries - triggering app recovery');
        onError();
      }
    } finally {
      if (retryCount === 0) { // Only set loading false on the first attempt
        setIsLoadingHistory(false);
      }
    }
  };

  const connectWebSocket = () => {
    try {
      const wsUrl = `wss://stream.binance.com:9443/ws/btcusdt@kline_${interval}`;
      
      if (wsRef.current) {
        wsRef.current.close();
      }

      console.log(`🔗 Connecting to WebSocket (attempt ${connectionAttempts + 1})...`);
      setConnectionAttempts(prev => prev + 1);

      wsRef.current = new WebSocket(wsUrl);

      wsRef.current.onopen = () => {
        console.log('✅ WebSocket connected successfully');
        setIsConnected(true);
        setHasError(false);
        reconnectAttempts.current = 0;
        setConnectionAttempts(0);
      };

      wsRef.current.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          const kline = data.k;
          
          if (kline) {
            const newCandle = {
              t: new Date(kline.t),
              o: parseFloat(kline.o),
              h: parseFloat(kline.h),
              l: parseFloat(kline.l),
              c: parseFloat(kline.c),
            };

            setCurrentPrice(newCandle.c);

            setCandleData(prevData => {
              const existingIndex = prevData.findIndex(
                candle => candle.t.getTime() === newCandle.t.getTime()
              );

              let updatedData;
              if (existingIndex >= 0) {
                updatedData = [...prevData];
                updatedData[existingIndex] = newCandle;
              } else {
                updatedData = [...prevData, newCandle];
              }

              if (updatedData.length > maxDataPoints) {
                updatedData = updatedData.slice(-maxDataPoints);
              }

              return updatedData;
            });
          }
        } catch (error) {
          console.error('Error parsing WebSocket message:', error);
        }
      };

      wsRef.current.onerror = (error) => {
        console.error('❌ WebSocket error:', error);
        setIsConnected(false);
        setHasError(true);
        
        // Call parent error handler for Safari issues
        if (connectionAttempts > 3 && onError) {
          console.log('🚨 Multiple connection failures - triggering app-level recovery');
          onError();
        }
      };

      wsRef.current.onclose = (event) => {
        console.log(`🔌 WebSocket disconnected (code: ${event.code})`);
        setIsConnected(false);
        
        // Don't reconnect if it's a deliberate close
        if (event.code !== 1000) {
          handleReconnect();
        }
      };
      
    } catch (error) {
      console.error('Failed to create WebSocket connection:', error);
      setHasError(true);
      setIsConnected(false);
      
      if (onError) {
        onError();
      }
    }
  };

  const handleReconnect = () => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
    }

    const delay = Math.min(1000 * Math.pow(2, reconnectAttempts.current), 30000);
    reconnectAttempts.current += 1;

    console.log(`Reconnecting in ${delay / 1000}s...`);

    reconnectTimeoutRef.current = setTimeout(() => {
      connectWebSocket();
    }, delay);
  };

  useEffect(() => {
    fetchHistoricalData();
    connectWebSocket();

    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
    };
  }, [interval]);

  const handleIntervalChange = (e) => {
    setInterval(e.target.value);
    setCandleData([]);
  };

  const chartData = {
    labels: candleData.map(d => d.t),
    datasets: [
      {
        label: 'BTC/USDT Close Price',
        data: candleData.map(d => ({
          x: d.t,
          y: d.c
        })),
        borderColor: 'rgb(75, 192, 192)',
        backgroundColor: 'rgba(75, 192, 192, 0.1)',
        tension: 0.1,
        pointRadius: 1,
        pointHoverRadius: 5,
        fill: true,
      },
      {
        label: 'High',
        data: candleData.map(d => ({
          x: d.t,
          y: d.h
        })),
        borderColor: 'rgba(34, 197, 94, 0.5)',
        backgroundColor: 'transparent',
        tension: 0.1,
        pointRadius: 0,
        borderWidth: 1,
        borderDash: [5, 5],
      },
      {
        label: 'Low',
        data: candleData.map(d => ({
          x: d.t,
          y: d.l
        })),
        borderColor: 'rgba(239, 68, 68, 0.5)',
        backgroundColor: 'transparent',
        tension: 0.1,
        pointRadius: 0,
        borderWidth: 1,
        borderDash: [5, 5],
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: 'top',
      },
      tooltip: {
        enabled: true,
        mode: 'index',
        intersect: false,
        callbacks: {
          label: (context) => {
            const dataIndex = context.dataIndex;
            const candle = candleData[dataIndex];
            if (candle) {
              return [
                `Open: $${candle.o.toFixed(2)}`,
                `High: $${candle.h.toFixed(2)}`,
                `Low: $${candle.l.toFixed(2)}`,
                `Close: $${candle.c.toFixed(2)}`,
              ];
            }
            return `Price: $${context.parsed.y.toFixed(2)}`;
          },
        },
      },
    },
    scales: {
      x: {
        type: 'time',
        time: {
          unit: 'minute',
          displayFormats: {
            minute: 'HH:mm',
            hour: 'HH:mm',
          },
        },
        title: {
          display: true,
          text: 'Time',
        },
      },
      y: {
        type: 'linear',
        title: {
          display: true,
          text: 'Price (USD)',
        },
        ticks: {
          callback: (value) => `$${value.toFixed(2)}`,
        },
      },
    },
  };

  return (
    <div className="w-full p-6 bg-gray-900 text-white rounded-lg shadow-xl">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-6 space-y-4 sm:space-y-0">
        <div>
          <h2 className="text-xl sm:text-2xl font-bold mb-2">Bitcoin Price Chart</h2>
          <div className="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-4">
            {currentPrice && (
              <div className="text-2xl sm:text-3xl font-bold text-green-400">
                ${currentPrice.toFixed(2)}
              </div>
            )}
            <div className="flex items-center space-x-2">
              <div className={`w-3 h-3 rounded-full ${isConnected ? 'bg-green-500' : 'bg-red-500'}`}></div>
              <span className="text-sm text-gray-400">
                {isConnected ? 'Connected' : 'Disconnected'}
              </span>
            </div>
          </div>
        </div>
        
        <div className="flex items-center space-x-2 self-start sm:self-auto">
          <label htmlFor="interval" className="text-sm text-gray-400 whitespace-nowrap">
            Interval:
          </label>
          <select
            id="interval"
            value={interval}
            onChange={handleIntervalChange}
            className="bg-gray-800 text-white px-2 sm:px-4 py-2 rounded border border-gray-700 focus:outline-none focus:border-blue-500 text-sm sm:text-base min-w-0 flex-shrink-0"
          >
            {intervalOptions.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="h-96 bg-gray-800 rounded p-4">
        {isLoadingHistory ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-gray-400">Loading historical data...</div>
          </div>
        ) : candleData.length > 0 ? (
          <Line data={chartData} options={chartOptions} />
        ) : (
          <div className="flex items-center justify-center h-full">
            <div className="text-gray-400">No data available</div>
          </div>
        )}
      </div>

      <div className="mt-4 text-sm text-gray-500 text-center">
        Showing last {Math.min(candleData.length, maxDataPoints)} candles
      </div>
    </div>
  );
};

export default BitcoinPriceChart;